/* ============================================================================
   SAKURA COLLECTORS CLUB — les pétales
   ============================================================================

   Élément personnalisé <oripaya-petales>, rendu par
   snippets/oripaya-petales.liquid. Aucune dépendance, aucun module importé,
   chargé en `defer`.

   POURQUOI UN CANVAS ET PAS DU CSS
   Une chute crédible demande trois mouvements simultanés et désynchronisés :
   descente, dérive latérale sinusoïdale, rotation avec retournement. En CSS
   il faudrait un élément par pétale, chacun promu sur sa propre couche de
   composition (18 couches à repeindre), plus une animation infinie par
   pétale que le navigateur ne peut jamais mettre en pause finement. Un canvas
   unique, c'est une seule couche, une seule boucle, et un arrêt immédiat dès
   que l'onglet passe en arrière-plan.

   BUDGET TENU
   - 8 à 18 pétales selon l'intensité. Jamais plus : au-delà, ce n'est plus
     une alcôve, c'est une boule à neige.
   - un seul requestAnimationFrame, arrêté hors viewport et hors onglet actif.
   - une seule forme Path2D, mise à l'échelle par transformation : aucune
     reconstruction de tracé par frame.
   - devicePixelRatio plafonné à 2 : au-delà, on quadruple la surface peinte
     pour une différence invisible sur une forme floue et translucide.
   - aucun scintillement : l'opacité de chaque pétale est fixée à sa naissance
     et ne varie jamais. Ce qui varie, c'est l'orientation.

   ACCESSIBILITÉ
   Décoratif de bout en bout : aria-hidden sur le conteneur (posé par le
   snippet), pointer-events: none sur le conteneur ET le canvas, et
   désactivation totale sous prefers-reduced-motion — le canvas n'est même pas
   créé, et s'il existait déjà il est retiré du DOM.
   ============================================================================ */

(function () {
  'use strict';

  /* Le script peut être inclus plusieurs fois si plusieurs sections appellent
     le snippet : on ne redéfinit jamais un élément déjà enregistré. */
  if (!window.customElements || customElements.get('oripaya-petales')) return;

  var REDUIT = window.matchMedia('(prefers-reduced-motion: reduce)');
  var ETROIT = window.matchMedia('(max-width: 749px)');

  /* Densités. Le nombre est un plafond absolu, pas une cible à densité égale :
     un hero de 90vh et une bande de 240px reçoivent le même compte, ce qui
     rend l'effet naturellement plus discret sur les grandes surfaces. */
  var DENSITE = { discret: 8, moyen: 12, soutenu: 18 };

  /* Opacité globale par défaut selon le fond. Sur ivoire, --sk-sakura-doux ne
     contraste qu'à 1,60:1 : à cette opacité il ne peut rien masquer, et il ne
     prétend rien signifier. Sur laque, on peut monter sans agresser. */
  var OPACITE = { laque: 0.5, clair: 0.26 };

  /* La forme. Un pétale de cerisier : pointe en haut, échancrure en bas,
     asymétrie légère pour qu'aucun pétale ne ressemble à une amande.
     Tracé en coordonnées unitaires, mis à l'échelle par ctx.scale(). */
  var FORME = new Path2D('M0-1C.62-.58.56.6 0 1 -.56.6 -.66-.58 0-1Z');

  function nb(v, defaut) {
    v = parseFloat(v);
    return isNaN(v) ? defaut : v;
  }

  function entre(min, max) {
    return min + Math.random() * (max - min);
  }

  var Petales = class extends HTMLElement {
    connectedCallback() {
      if (this._monte) return;
      this._monte = true;

      this._petales = [];
      this._visible = true;
      this._onglet = document.visibilityState !== 'hidden';
      this._t = 0;

      this._surPref = this._evaluer.bind(this);
      this._surOnglet = function () {
        this._onglet = document.visibilityState !== 'hidden';
        /* On repart de zéro pour le delta : sans cela, revenir sur l'onglet
           au bout de dix minutes téléporterait les pétales en bas de l'écran. */
        this._t = 0;
        this._cadencer();
      }.bind(this);

      /* Un changement de réglage système ou une rotation de téléphone doivent
         être pris en compte sans rechargement. */
      if (REDUIT.addEventListener) {
        REDUIT.addEventListener('change', this._surPref);
        ETROIT.addEventListener('change', this._surPref);
      }
      document.addEventListener('visibilitychange', this._surOnglet);

      this._evaluer();
    }

    disconnectedCallback() {
      this._monte = false;
      /* Purge complète : on ne laisse ni canvas ni observateur accrochés à un
         nœud détaché. L'élément se reconstruit tout seul s'il est remis dans
         la page (réordonnancement de sections dans l'éditeur de thème). */
      this._arreter(true);
      if (REDUIT.removeEventListener) {
        REDUIT.removeEventListener('change', this._surPref);
        ETROIT.removeEventListener('change', this._surPref);
      }
      document.removeEventListener('visibilitychange', this._surOnglet);
    }

    /* ---- Décide si l'effet a le droit de tourner ------------------------ */
    _evaluer() {
      var d = this.dataset;
      var permis =
        d.actif !== 'false' &&
        !REDUIT.matches &&
        !(d.mobile === 'false' && ETROIT.matches);

      if (permis) this._demarrer();
      else this._arreter(true);
    }

    _demarrer() {
      if (this._ctx) return this._cadencer();

      var cv = document.createElement('canvas');
      /* Le canvas est déjà dans un conteneur aria-hidden ; on le redit ici
         pour le cas où un agent poserait l'élément sans passer par le snippet. */
      cv.setAttribute('aria-hidden', 'true');
      this.appendChild(cv);

      this._cv = cv;
      this._ctx = cv.getContext('2d', { alpha: true });
      if (!this._ctx) return; /* canvas indisponible : on ne casse rien */

      var style = getComputedStyle(this);
      this._couleur = (style.getPropertyValue('--sk-sakura-doux') || '').trim() || '#E8B7BF';

      var teinte = this.dataset.teinte;
      if (!teinte || teinte === 'auto') {
        teinte = this.closest('.sk-vitrine') ? 'laque' : 'clair';
      }
      this._alpha = nb(this.dataset.opacite, OPACITE[teinte] || OPACITE.clair);
      this._echelle = nb(this.dataset.taille, 1);
      this._compte = DENSITE[this.dataset.intensite] || DENSITE.moyen;

      this._dimensionner();
      this._semer();

      /* Hors viewport, la boucle s'arrête complètement. La marge de 15 %
         évite un redémarrage saccadé juste au bord de l'écran. */
      if (window.IntersectionObserver) {
        this._io = new IntersectionObserver(
          function (entrees) {
            this._visible = entrees[0].isIntersecting;
            this._t = 0;
            this._cadencer();
          }.bind(this),
          { rootMargin: '15%' }
        );
        this._io.observe(this);
      }

      /* ResizeObserver plutôt qu'un écouteur de redimensionnement : il capte
         aussi les changements de hauteur dus au contenu de la section, qu'un
         événement `resize` ne verrait jamais. */
      if (window.ResizeObserver) {
        this._ro = new ResizeObserver(this._dimensionner.bind(this));
        this._ro.observe(this);
      }

      /* data-anime est l'état d'exécution, distinct de data-actif qui est
         l'interrupteur du marchand : le CSS ne révèle le canvas qu'une fois
         la première frame prête, ce qui évite un rectangle vide qui s'allume. */
      this.dataset.anime = 'true';
      this._cadencer();
    }

    /* purger = true retire le canvas (cas du mouvement réduit activé en cours
       de session, ou du passage en mobile quand l'effet y est désactivé). */
    _arreter(purger) {
      if (this._rafId) {
        cancelAnimationFrame(this._rafId);
        this._rafId = 0;
      }
      if (!purger) return;

      if (this._io) { this._io.disconnect(); this._io = null; }
      if (this._ro) { this._ro.disconnect(); this._ro = null; }
      if (this._cv && this._cv.parentNode) this._cv.parentNode.removeChild(this._cv);
      this._cv = null;
      this._ctx = null;
      this._petales = [];
      this.removeAttribute('data-anime');
    }

    /* ---- Dimensions ------------------------------------------------------
       Le canvas suit son conteneur en pixels physiques. La transformation est
       réinitialisée à chaque redimensionnement : setTransform, jamais scale
       cumulé. */
    _dimensionner() {
      if (!this._ctx) return;
      var r = this.getBoundingClientRect();
      var dpr = Math.min(window.devicePixelRatio || 1, 2);

      this._l = Math.max(1, Math.round(r.width));
      this._h = Math.max(1, Math.round(r.height));

      this._dpr = dpr;
      this._cv.width = Math.round(this._l * dpr);
      this._cv.height = Math.round(this._h * dpr);
      this._ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      this._ctx.fillStyle = this._couleur;
    }

    _naitre(p, hautDeLEcran) {
      p.t = entre(5, 13) * this._echelle;          /* demi-taille en px      */
      p.x = entre(-20, this._l + 20);
      p.y = hautDeLEcran ? entre(-this._h * 0.2, -p.t) : entre(0, this._h);
      p.v = entre(14, 34);                          /* chute, px/s            */
      p.a = entre(8, 26);                           /* amplitude de dérive    */
      p.f = entre(0.14, 0.38);                      /* fréquence de dérive Hz */
      p.ph = Math.random() * 6.283;                 /* phase                  */
      p.r = Math.random() * 6.283;                  /* rotation dans le plan  */
      p.vr = entre(-0.5, 0.5);                      /* vitesse de rotation    */
      p.tr = Math.random() * 6.283;                 /* angle de retournement  */
      p.vt = entre(0.25, 0.7);                      /* vitesse de retournement*/
      p.o = entre(0.45, 1);                         /* opacité relative fixe  */
      return p;
    }

    _semer() {
      var n = this._compte;
      var liste = this._petales;
      liste.length = 0;
      for (var i = 0; i < n; i++) liste.push(this._naitre({}, false));
    }

    _cadencer() {
      var doitTourner = this._ctx && this._visible && this._onglet && this._monte;
      if (doitTourner && !this._rafId) {
        this._rafId = requestAnimationFrame(this._frame.bind(this));
      } else if (!doitTourner && this._rafId) {
        cancelAnimationFrame(this._rafId);
        this._rafId = 0;
      }
    }

    _frame(horodatage) {
      this._rafId = 0;
      if (!this._ctx) return;

      /* Delta plafonné à 50 ms : après un blocage du fil principal (une image
         lourde décodée, une section rechargée par l'éditeur), les pétales
         reprennent leur course au lieu de sauter d'un demi-écran. */
      var dt = this._t ? Math.min((horodatage - this._t) / 1000, 0.05) : 0.016;
      this._t = horodatage;

      var ctx = this._ctx;
      var l = this._l;
      var h = this._h;
      var dpr = this._dpr;
      var liste = this._petales;

      /* Un vent commun, très lent, qui donne à la chute sa cohérence : sans
         lui, chaque pétale a l'air de tomber dans sa propre pièce. */
      var vent = Math.sin(horodatage * 0.00012) * 10;

      ctx.clearRect(0, 0, l, h);
      ctx.fillStyle = this._couleur;

      for (var i = 0; i < liste.length; i++) {
        var p = liste[i];

        p.y += p.v * dt;
        p.x += vent * dt;
        p.ph += p.f * dt * 6.283;
        p.r += p.vr * dt;
        p.tr += p.vt * dt;

        if (p.y - p.t > h) this._naitre(p, true);
        else if (p.x < -60) p.x = l + 40;
        else if (p.x > l + 60) p.x = -40;

        /* Le retournement : le pétale présente sa tranche puis sa face. C'est
           un simple écrasement horizontal — jamais une variation d'opacité,
           qui produirait le scintillement explicitement proscrit. */
        var face = Math.cos(p.tr);
        var plat = 0.2 + 0.8 * Math.abs(face);

        ctx.globalAlpha = this._alpha * p.o;
        /* On repart TOUJOURS de la matrice du device pixel ratio, jamais de
           l'identité : sinon le tracé serait deux fois trop petit sur écran
           HiDPI, et les transformations du pétale précédent s'ajouteraient. */
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        ctx.translate(p.x + Math.sin(p.ph) * p.a, p.y);
        ctx.rotate(p.r);
        ctx.scale(p.t * plat, p.t);
        ctx.fill(FORME);
      }

      /* Matrice rendue propre pour le clearRect de la frame suivante. */
      ctx.globalAlpha = 1;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

      this._cadencer();
    }
  };

  customElements.define('oripaya-petales', Petales);
})();
